// src/components/proposal/CheckpointGenerationFlow.tsx
// Container for the human-in-the-loop, checkpoint-gated generation flow.
// Owns the GenerationSession, renders a clickable gate stepper + PhaseReviewPanel,
// and reports its status to the parent (for the minimized sticky bar).
//
// Auto-run rule (deliberate): a gate generates AT MOST ONCE per user action and
// nothing ever re-runs itself. The only auto-runs are (1) the first gate when a
// FRESH session is created (the user clicked Generate) and (2) the next gate right
// after the user Approves the prior one. A RESUMED session never auto-runs — the
// user sees the current state and chooses. If a gate errors or was interrupted
// mid-generation, it STOPS and offers "Resume from last state" or "Abandon".

import React, { useEffect, useRef, useState } from 'react';
import { Check, Loader2, Sparkles } from 'lucide-react';
import {
  OrchestratedProposalService,
  GenerationSession,
  GenerationPhaseId,
  GeneratedProposal,
  GENERATION_PHASE_ORDER,
  GENERATION_PHASE_META,
} from '../../services/orchestrated-proposal-service';
import { PhaseReviewPanel, UserCorrection, getEditableSections } from './PhaseReviewPanel';
import { SectionFeedbackDialog, SectionFeedback } from './SectionFeedbackDialog';
import { extractDataNeeded, DataNeededItem } from '../../lib/section-content-utils';
import toast from 'react-hot-toast';

// Snapshot of the flow's state, reported to the parent so the minimized sticky bar
// can show the current gate + whether it is generating or ready to review.
export interface CheckpointStatus {
  phaseLabel: string;
  generating: boolean;
  awaitingReview: boolean;
  // The gate stopped and needs the user: it errored or was interrupted.
  stopped: boolean;
}

interface CheckpointGenerationFlowProps {
  service: OrchestratedProposalService;
  initialSession: GenerationSession;
  // True when the session was loaded via Resume; suppresses any auto-run so a
  // resumed flow never re-fires a gate on its own.
  isResume?: boolean;
  onFinalized: (templateId: string, proposal: GeneratedProposal) => void;
  // Called when the user abandons the session (from an errored/interrupted gate).
  onAbandon: () => void;
  // Reports gate/status changes so the parent's minimized bar stays in sync.
  onStatusChange?: (status: CheckpointStatus) => void;
}

type ViewTarget = GenerationPhaseId | 'finalize';

export const CheckpointGenerationFlow: React.FC<CheckpointGenerationFlowProps> = ({
  service,
  initialSession,
  isResume = false,
  onFinalized,
  onAbandon,
  onStatusChange,
}) => {
  const [session, setSession] = useState<GenerationSession>(initialSession);
  const [viewPhase, setViewPhase] = useState<ViewTarget>(initialSession.currentPhase);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The gate THIS component is actively generating right now. Used to tell an
  // in-progress gate apart from one stuck in `generating` after an interruption.
  const [runningPhase, setRunningPhase] = useState<GenerationPhaseId | null>(null);
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const runningRef = useRef(false);
  const didInitRef = useRef(false);

  // Global corrections recorded against this session (compound into every section).
  const corrections: UserCorrection[] =
    ((session.collectedData?.additionalContext as any)?.userCorrections as UserCorrection[]) || [];

  const currentPhase = session.currentPhase; // the frontier
  const isFinalizeView = viewPhase === 'finalize';

  // Follow the frontier as it advances (after an approval), unless the user has
  // deliberately navigated back to an earlier gate.
  useEffect(() => {
    setViewPhase(currentPhase);
  }, [currentPhase]);

  // Run exactly ONE gate. Guarded so two runs can never overlap. On error the
  // service has already persisted the phase as `error`; we surface it and STOP —
  // no retry loop. Returns the resulting session.
  const runPhase = async (phaseId: GenerationPhaseId, sess: GenerationSession): Promise<GenerationSession> => {
    if (runningRef.current) return sess;
    runningRef.current = true;
    setRunningPhase(phaseId);
    setBusy(true);
    setError(null);
    try {
      const updated = await service.runPhase(phaseId, sess);
      setSession({ ...updated });
      return updated;
    } catch (e: any) {
      setError(e?.message || 'Generation failed');
      // The service mutated + persisted `sess` to status 'error'; reflect it.
      setSession({ ...sess });
      return sess;
    } finally {
      runningRef.current = false;
      setRunningPhase(null);
      setBusy(false);
    }
  };

  // One-time: a FRESH session auto-runs its first ungenerated gate (the user just
  // clicked Generate). A resumed session never auto-runs.
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    if (isResume) return;
    if (currentPhase === 'finalize') return;
    const st = session.phases[currentPhase]?.status;
    if (!st || st === 'not_started') {
      void runPhase(currentPhase as GenerationPhaseId, session);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Report status to the parent (drives the minimized sticky bar).
  useEffect(() => {
    if (!onStatusChange) return;
    if (currentPhase === 'finalize') {
      onStatusChange({ phaseLabel: 'Finalize', generating: false, awaitingReview: true, stopped: false });
      return;
    }
    const st = session.phases[currentPhase]?.status;
    const generating = runningPhase === currentPhase;
    onStatusChange({
      phaseLabel: GENERATION_PHASE_META[currentPhase].label,
      generating,
      awaitingReview: !generating && (st === 'awaiting_review' || st === 'needs_input'),
      // Errored, or stuck `generating` with nothing running it (interrupted).
      stopped: !generating && (st === 'error' || st === 'generating'),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, runningPhase, currentPhase]);

  const handleApprove = async () => {
    setBusy(true);
    setError(null);
    try {
      const updated = await service.approvePhase(session, viewPhase as GenerationPhaseId);
      setSession({ ...updated });
      // User-initiated: kick the newly-revealed frontier gate ONCE.
      const next = updated.currentPhase;
      if (next !== 'finalize') {
        const st = updated.phases[next]?.status;
        if (!st || st === 'not_started') {
          await runPhase(next as GenerationPhaseId, updated);
        }
      }
    } catch (e: any) {
      setError(e?.message || 'Approval failed');
      toast.error(e?.message || 'Approval failed');
    } finally {
      setBusy(false);
    }
  };

  const handleRegenerate = async () => {
    if (runningRef.current) return;
    const target = viewPhase as GenerationPhaseId;
    setRunningPhase(target);
    runningRef.current = true;
    setBusy(true);
    setError(null);
    try {
      const updated = await service.regeneratePhase(session, target);
      setSession({ ...updated });
      setViewPhase(target); // stay on the gate we just regenerated
    } catch (e: any) {
      setError(e?.message || 'Regeneration failed');
      setSession({ ...session });
    } finally {
      runningRef.current = false;
      setRunningPhase(null);
      setBusy(false);
    }
  };

  // "Resume from last state": one explicit retry of THIS gate, built on the
  // already-approved upstream gates. Not a loop — it runs once when the user asks.
  const handleResumePhase = async () => {
    await runPhase(viewPhase as GenerationPhaseId, session);
  };

  // Abandon the whole session (from an errored/interrupted gate) and close out.
  const handleAbandon = async () => {
    setBusy(true);
    try {
      await service.abandonSession(session);
    } catch (e) {
      console.warn('Abandon failed:', e);
    } finally {
      setBusy(false);
    }
    onAbandon();
  };

  const handleAnswer = async (questionId: string, answer: string) => {
    const updated = await service.answerQuestion(session, questionId, answer);
    setSession({ ...updated });
  };

  const handleEdit = async (key: string, value: string) => {
    const updated = await service.saveSubSectionEdit(session, viewPhase as GenerationPhaseId, key, value);
    setSession({ ...updated });
  };

  // Record a free-text correction globally, then regenerate ONLY this gate with it
  // applied (downstream gates reset, not re-run).
  const handleSubmitFeedback = async (feedback: SectionFeedback) => {
    if (runningRef.current) return;
    const target = viewPhase as GenerationPhaseId;
    setShowFeedbackDialog(false);
    setRunningPhase(target);
    runningRef.current = true;
    setBusy(true);
    setError(null);
    try {
      const updated = await service.regeneratePhaseWithFeedback(session, target, feedback.userFeedback);
      setSession({ ...updated });
      setViewPhase(target);
      toast.success('Feedback applied — this step was regenerated.');
    } catch (e: any) {
      setError(e?.message || 'Refine failed');
      setSession({ ...session });
      toast.error(e?.message || 'Refine failed');
    } finally {
      runningRef.current = false;
      setRunningPhase(null);
      setBusy(false);
    }
  };

  const handleRemoveCorrection = async (id: string) => {
    const updated = await service.removeCorrection(session, id);
    setSession({ ...updated });
  };

  const handleFinalize = async () => {
    setBusy(true);
    setError(null);
    try {
      const { proposal, templateId } = await service.finalizeSession(session);
      toast.success('Proposal finalized!');
      onFinalized(templateId, proposal);
    } catch (e: any) {
      setError(e?.message || 'Finalize failed');
      toast.error(e?.message || 'Finalize failed');
    } finally {
      setBusy(false);
    }
  };

  // A gate is "reached" (and therefore navigable) once it has been started or the
  // frontier has passed it.
  const isReached = (p: GenerationPhaseId): boolean => {
    if (p === 'gap-resolution') return true;
    if (session.phases[p]?.status) return true;
    if (currentPhase === 'finalize') return true;
    return GENERATION_PHASE_ORDER.indexOf(p) <= GENERATION_PHASE_ORDER.indexOf(currentPhase as GenerationPhaseId);
  };

  const renderStepper = () => (
    <div className="flex items-center flex-wrap gap-y-2 mb-5">
      {GENERATION_PHASE_ORDER.map((p, idx) => {
        const st = p === 'gap-resolution' ? 'approved' : session.phases[p]?.status;
        const approved = p === 'gap-resolution' ? true : !!session.phases[p]?.approved;
        const isViewing = !isFinalizeView && p === viewPhase;
        const reached = isReached(p);
        const navigable = reached && p !== 'gap-resolution';
        const tone = approved
          ? 'bg-success-bg0 text-white'
          : isViewing
            ? 'bg-bw-primary text-white'
            : st === 'generating'
              ? 'bg-bw-primary-tint text-bw-primary'
              : 'bg-bw-surface-subtle text-bw-muted';
        return (
          <React.Fragment key={p}>
            <button
              type="button"
              disabled={!navigable || busy}
              onClick={() => navigable && setViewPhase(p)}
              className={`flex items-center gap-1.5 ${navigable ? 'cursor-pointer' : 'cursor-default'} ${isViewing ? '' : 'opacity-90'}`}
              title={GENERATION_PHASE_META[p].label}
            >
              <span className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold ${tone}`}>
                {approved ? <Check className="w-3.5 h-3.5" /> : st === 'generating' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : idx}
              </span>
              <span className={`text-xs ${isViewing ? 'font-semibold text-bw-ink' : approved ? 'text-bw-muted' : 'text-bw-muted'}`}>
                {GENERATION_PHASE_META[p].label}
              </span>
            </button>
            {idx < GENERATION_PHASE_ORDER.length - 1 && (
              <div className={`flex-1 min-w-[12px] h-px mx-2 ${approved ? 'bg-emerald-300' : 'bg-gray-200'}`} />
            )}
          </React.Fragment>
        );
      })}
      {/* Finalize node, available once all gates are approved */}
      {currentPhase === 'finalize' && (
        <>
          <div className="flex-1 min-w-[12px] h-px mx-2 bg-emerald-300" />
          <button
            type="button"
            onClick={() => setViewPhase('finalize')}
            disabled={busy}
            className="flex items-center gap-1.5 cursor-pointer"
            title="Finalize"
          >
            <span className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold ${isFinalizeView ? 'bg-bw-primary text-white' : 'bg-bw-surface-subtle text-bw-muted'}`}>
              <Sparkles className="w-3.5 h-3.5" />
            </span>
            <span className={`text-xs ${isFinalizeView ? 'font-semibold text-bw-ink' : 'text-bw-muted'}`}>Finalize</span>
          </button>
        </>
      )}
    </div>
  );

  const viewState = session.phases[viewPhase as GenerationPhaseId] || { status: 'not_started' as const, approved: false };
  // Everything the AI flagged as "[DATA NEEDED]" across this gate's sub-sections, so
  // the Refine dialog can surface it and the user can ask the AI to fill it in.
  const dataNeeded: DataNeededItem[] = isFinalizeView
    ? []
    : getEditableSections(viewPhase as GenerationPhaseId, viewState)
        .filter(s => s.editable)
        .flatMap(s => extractDataNeeded(s.content));
  // The viewed gate shows `generating` but this component isn't the one running it
  // → it was interrupted (e.g. the browser left mid-generation). Offer Resume/Abandon.
  const interrupted = viewState.status === 'generating' && runningPhase !== viewPhase;

  return (
    <div>
      {renderStepper()}

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-urgent-bg px-4 py-3 text-sm text-urgent">
          {error}
        </div>
      )}

      {isFinalizeView ? (
        <div className="text-center py-12">
          <div className="w-14 h-14 bg-success-bg rounded-full flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-7 h-7 text-success" />
          </div>
          <h3 className="text-lg font-semibold text-bw-ink mb-1">All sections approved</h3>
          <p className="text-bw-muted mb-6 max-w-md mx-auto text-sm">
            Finalize to run a last AI review and assemble your approved sections into the
            editable proposal. You can still click a gate above to revise it first.
          </p>
          <button
            onClick={handleFinalize}
            disabled={busy}
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition-colors text-sm font-medium disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Finalize &amp; open proposal
          </button>
        </div>
      ) : (
        <PhaseReviewPanel
          phaseId={viewPhase as GenerationPhaseId}
          phaseState={viewState}
          questions={session.questions}
          busy={busy}
          interrupted={interrupted}
          isLastPhase={viewPhase === 'summary-cover'}
          onApprove={handleApprove}
          onRegenerate={handleRegenerate}
          onResume={handleResumePhase}
          onAbandon={handleAbandon}
          onAnswer={handleAnswer}
          onEdit={handleEdit}
          onRefineWithFeedback={() => setShowFeedbackDialog(true)}
          corrections={corrections}
          onRemoveCorrection={handleRemoveCorrection}
        />
      )}

      {!isFinalizeView && (
        <SectionFeedbackDialog
          isOpen={showFeedbackDialog}
          onClose={() => setShowFeedbackDialog(false)}
          onSubmit={handleSubmitFeedback}
          sectionTitle={GENERATION_PHASE_META[viewPhase as GenerationPhaseId].label}
          sectionType={viewPhase}
          isSubmitting={busy}
          mode="refine"
          dataNeededItems={dataNeeded}
        />
      )}
    </div>
  );
};

export default CheckpointGenerationFlow;
